import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { styles, ms, css, statusColor, trackEvent, setPageMeta } from "./shared";
import Nav from "./Nav";
import Footer from "./Footer";

export default function PropertyPage() {
  const { slug } = useParams();
  const [developments, setDevelopments] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    fetch("/developments.json")
      .then(r => r.json())
      .then(data => setDevelopments(data))
      .catch(() => setDevelopments([]));
  }, []);

  const dev = developments ? developments.find((d) => d.slug === slug) : undefined;

  useEffect(() => {
    setActiveImg(0);
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (!dev) return;
    setPageMeta({
      title: `${dev.name} — ${dev.price} | Nekretnine u Španiji`,
      description: `${dev.name} — nekretnina na prodaju u ${dev.location}. ${dev.rooms}, ${dev.price}. ${dev.description.slice(0, 120).trim()}…`,
      path: `/nekretnine/${dev.slug}`,
    });
  }, [dev]);

  useEffect(() => {
    if (dev) trackEvent("view_item", { property: dev.name });
  }, [dev]);

  if (developments && !dev) {
    return (
      <div style={styles.root}>
        <style>{css}</style>
        <Nav />
        <div style={styles.notFoundWrap}>
          <h1 style={{ ...styles.sectionTitle, margin: 0 }}>Nekretnina nije pronađena</h1>
          <p style={{ color: "#a8a8a8" }}>Ova nekretnina možda više nije u ponudi, ili je link netačan.</p>
          <Link to="/#nekretnine" style={styles.btnPrimary}>Pogledajte sve nekretnine</Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!dev) {
    return (
      <div style={styles.root}>
        <style>{css}</style>
        <Nav />
        <div style={styles.notFoundWrap}>
          <span style={{ color: "#9a9a9a", fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.16em", fontSize: 13 }}>UČITAVANJE...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.root}>
      <style>{css}</style>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Apartment",
        name: dev.name,
        description: dev.description,
        url: `https://nekretnineuspaniji.com/nekretnine/${dev.slug}`,
        image: dev.images,
        address: { "@type": "PostalAddress", addressLocality: dev.location, addressCountry: "ES" },
        offers: { "@type": "Offer", price: dev.price.replace(/[^\d]/g, ""), priceCurrency: "EUR", availability: "https://schema.org/InStock" },
      }) }} />

      <Nav />

      <div style={styles.pageHeader}>
        <p style={styles.breadcrumbs}>
          <Link to="/" style={styles.breadcrumbLink}>Početna</Link> {" / "}
          <Link to="/#nekretnine" style={styles.breadcrumbLink}>Nekretnine</Link> {" / "}
          {dev.name}
        </p>
      </div>

      <section style={{ ...styles.section, paddingTop: 0 }} className="section-pad">
        <div style={ms.inner} className="modal-inner">
          <div style={ms.imgCol}>
            <div style={ms.mainImgWrap} className="modal-main-img">
              <img src={dev.images[activeImg]} alt={`${dev.name} — ${dev.location}, fotografija ${activeImg + 1}`} style={ms.mainImg} key={activeImg} className="modal-img-fade" />
              <div style={ms.imgOverlay} />
              <span style={{ ...ms.badge, background: statusColor[dev.status] || "#c9a96e" }}>{dev.status}</span>
              {activeImg > 0 && <button style={{ ...ms.arrow, left: 16 }} onClick={() => setActiveImg((i) => i - 1)} aria-label="Prethodna slika">‹</button>}
              {activeImg < dev.images.length - 1 && <button style={{ ...ms.arrow, right: 16 }} onClick={() => setActiveImg((i) => i + 1)} aria-label="Sledeća slika">›</button>}
              <div style={ms.imgCounter}>{activeImg + 1} / {dev.images.length}</div>
            </div>
            <div style={ms.thumbRow} className="thumb-row">
              {dev.images.map((img, i) => (
                <div key={i} style={{ ...ms.thumb, outline: activeImg === i ? "2px solid #c9a96e" : "2px solid transparent", outlineOffset: -2 }} onClick={() => setActiveImg(i)}>
                  <img src={img} alt="" loading="lazy" style={ms.thumbImg} />
                </div>
              ))}
            </div>
          </div>
          <div style={ms.infoCol}>
            <p style={ms.infoEyebrow}>{dev.type} · {dev.rooms}</p>
            <h1 style={{ ...ms.infoTitle, fontSize: 36 }}>{dev.name}</h1>
            <p style={ms.infoPrice}>{dev.price}</p>
            <div style={ms.divider} />
            <div style={ms.specsRow}>
              {[["🛏", dev.rooms], ["👁", dev.view], ["📍", dev.nearby], ["🗓", dev.completion]].map(([icon, val]) => (
                <div key={val} style={ms.spec}><span style={ms.specIcon}>{icon}</span><span style={ms.specVal}>{val}</span></div>
              ))}
            </div>
            <div style={ms.divider} />
            <p style={ms.description}>{dev.description}</p>
            <div style={ms.divider} />
            <p style={ms.featuresTitle}>SADRŽAJI I POGODNOSTI</p>
            <div style={ms.featuresGrid}>
              {dev.amenities.map((f) => (
                <div key={f} style={ms.featureItem}><span style={ms.featureDot} />{f}</div>
              ))}
            </div>
            <Link
              to={`/?property=${encodeURIComponent(dev.name)}#kontakt`}
              style={ms.enquireBtn}
              onClick={() => trackEvent("property_interest", { property: dev.name })}
            >
              ZAINTERESOVAN/A SAM ZA OVU NEKRETNINU
            </Link>
            <a
              href={`https://wa.me/34658372004?text=${encodeURIComponent(`Zdravo, zanima me nekretnina ${dev.name}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...styles.btnGhost, width: "100%", marginTop: 12 }}
              onClick={() => trackEvent("generate_lead", { method: "whatsapp", property: dev.name })}
            >
              PIŠITE NAM NA WHATSAPP
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
